import { z } from "zod";
import { type AiProposalRequest, aiFocusSchema, aiModelChoiceSchema, uuidSchema } from "./domain";
import { zonedParts, zonedTimeToInstant } from "./zoned-time";

/**
 * Automatic proposals start only in these local hours of the workspace, every
 * day: at night nobody would approve them (owner, 2026-10-22, ADR 0077).
 */
export const AI_WORKING_HOURS = {
  start: { hour: 8, minute: 30 },
  end: { hour: 19, minute: 0 },
} as const;

/** `instant` when it falls in the working hours, otherwise the next opening (8:30). */
export function nextAiWorkingTime(instant: Date, timeZone: string): Date {
  const local = zonedParts(instant, timeZone);
  const minutes = local.hour * 60 + local.minute;
  const { start, end } = AI_WORKING_HOURS;
  const opening = start.hour * 60 + start.minute;
  if (minutes >= opening && minutes < end.hour * 60 + end.minute) return instant;
  return zonedTimeToInstant(
    {
      year: local.year,
      month: local.month,
      day: minutes < opening ? local.day : local.day + 1,
      hour: start.hour,
      minute: start.minute,
    },
    timeZone,
  );
}

/**
 * Why OpenAI refuses every request, not just one (ADR 0072): the credit is
 * over, the key is refused, or it keeps answering 429 or 5xx.
 */
export const AI_PROVIDER_PROBLEMS = ["quota", "key", "rate_limit", "unavailable"] as const;
export const aiProviderProblemSchema = z.enum(AI_PROVIDER_PROBLEMS);
export type AiProviderProblem = z.infer<typeof aiProviderProblemSchema>;

/** What the AI is doing for a lead: kept in Redis by the workers, read by the API. */
export const LEAD_AI_STATES = ["scheduled", "running", "skipped", "failed", "done"] as const;
export const leadAiStateSchema = z.enum(LEAD_AI_STATES);
export type LeadAiState = z.infer<typeof leadAiStateSchema>;

export const aiRetryRequestSchema = z
  .object({
    focus: aiFocusSchema,
    instructions: z.string().max(2_000).nullable(),
    triggerMessageId: uuidSchema.nullable(),
    /** Missing in the records written before ADR 0077. */
    model: aiModelChoiceSchema.nullable().optional(),
  })
  .strict();
export type AiRetryRequest = z.infer<typeof aiRetryRequestSchema>;

/** What "Riprova" sends back to the API: the same request, model included. */
export function aiRetryProposalRequest(retry: AiRetryRequest): AiProposalRequest {
  return {
    focus: retry.focus,
    ...(retry.instructions ? { instructions: retry.instructions } : {}),
    ...(retry.triggerMessageId ? { triggerMessageId: retry.triggerMessageId } : {}),
    ...(retry.model ? { model: retry.model } : {}),
  };
}

export const leadAiStatusRecordSchema = z
  .object({
    state: leadAiStateSchema,
    /** The job's idempotency key: a job only overwrites itself or older jobs. */
    jobKey: z.string().min(1).max(256),
    /** When the job was queued (ms): the newest job owns the status. */
    queuedAt: z.number().int().nonnegative(),
    at: z.string().datetime(),
    /** "scheduled": when the analysis is expected to start. */
    runAt: z.string().datetime().nullable(),
    /** "skipped" and "failed": the reason, already readable. */
    reason: z.string().max(500).nullable(),
    problem: aiProviderProblemSchema.nullable(),
    /** "failed": what "Riprova" asks again. */
    retry: aiRetryRequestSchema.nullable(),
  })
  .strict();
export type LeadAiStatusRecord = z.infer<typeof leadAiStatusRecordSchema>;

export const aiProviderHealthRecordSchema = z
  .object({
    problem: aiProviderProblemSchema,
    since: z.string().datetime(),
    lastAt: z.string().datetime(),
    status: z.number().int().nullable(),
    /** OpenAI's error code, e.g. `insufficient_quota`. */
    code: z.string().max(64).nullable(),
  })
  .strict();
export type AiProviderHealthRecord = z.infer<typeof aiProviderHealthRecordSchema>;

export type LeadAiStatusDto = {
  state: Exclude<LeadAiState, "done">;
  since: string;
  startsAt: string | null;
  reason: string | null;
  problem: AiProviderProblem | null;
  retry: AiProposalRequest | null;
};

/** A chat left without a proposal, for the banner to name and link it. */
export type AiFailedChatDto = {
  leadId: string;
  displayName: string;
  failedAt: string;
  reason: string | null;
  retry: AiProposalRequest;
};

/** For owners and admins: the AI stopped for everyone, and the chats left without a proposal. */
export type AiHealthDto = {
  problem: { kind: AiProviderProblem; since: string; lastAt: string } | null;
  failedLeads: number;
  /** The newest of them, at most 20. */
  failedChats: AiFailedChatDto[];
};
